// frontend/src/pages/Home.jsx


import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { ArrowRight, PlayCircle, X, ChevronLeft, ChevronRight, MapPin, Clock, Users, Star } from 'lucide-react';
import ProfessionalSearchBar from '../components/FuturisticSearchBar';
import { useTourBuilder } from '../context/TourBuilderContext';
import { GET_HOME_FEATURED_TOURS, GET_TOUR_COUNTRIES } from '../graphql/tours';
import { GET_SITE_SETTINGS, GET_ABOUT_STATS, GET_ABOUT_PAGE_SETTINGS, GET_PRESS_PARTNERS } from '../graphql/coresite';


/* ─── Design tokens ─── */
const GOLD = '#B8975A';
const NAVY = '#080e1a';

/* ─── Hero media ───
   Ambient loop behind the hero copy (light 360p file) and the full-quality
   cut the "Watch Film" button opens — same footage, so the modal reads as
   a continuation of the background rather than a jump to something else. */
const HERO_BG_VIDEO   = 'https://assets.mixkit.co/videos/3880/3880-360.mp4';
const HERO_FILM_VIDEO = 'https://assets.mixkit.co/videos/3880/3880-720.mp4';
const HERO_POSTER     = 'https://images.unsplash.com/photo-1761078206756-68d3023f3021?w=1600&q=80';

const getHeroImage = (country) =>
  country.heroImage || country.flagImage || '';

const CONTINENT_LABELS = {
  africa:   'Africa',
  europe:   'Europe',
  asia:     'Asia',
  americas: 'The Americas',
  oceania:  'Oceania',
};

const getSubLabel = (country) =>
  CONTINENT_LABELS[country.frontendContinentSlug] ||
  country.frontendContinentSlug ||
  'Destination';

const HomeStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');

    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }

    @keyframes heroReveal {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .hero-line-1 { animation: heroReveal 1s  cubic-bezier(0.22,1,0.36,1) 0.2s both; }
    .hero-line-2 { animation: heroReveal 1s  cubic-bezier(0.22,1,0.36,1) 0.4s both; }
    .hero-line-3 { animation: heroReveal 1s  cubic-bezier(0.22,1,0.36,1) 0.6s both; }
    .hero-line-4 { animation: heroReveal 0.8s cubic-bezier(0.22,1,0.36,1) 0.8s both; }

    .grain-overlay {
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
    }

    .tour-card-home {
      flex: 0 0 340px;
      position: relative;
      background: #fff;
      border: 1px solid #e8e4de;
      overflow: hidden;
      transition: transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s cubic-bezier(0.22,1,0.36,1);
      scroll-snap-align: start;
    }
    .tour-card-home:hover {
      transform: translateY(-4px);
      box-shadow: 0 28px 80px -16px rgba(0,0,0,0.15);
    }
    .tour-card-home:hover .tc-img img { transform: scale(1.06); }
    .tc-img { overflow: hidden; height: 240px; flex-shrink: 0; }
    .tc-img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.8s cubic-bezier(0.22,1,0.36,1); }

    .tc-img-placeholder {
      width: 100%; height: 100%;
      background: linear-gradient(135deg, #1e2a3a 0%, #0f1827 100%);
      display: flex; align-items: center; justify-content: center;
    }

    .reserve-btn-home {
      position: relative;
      font-family: 'Jost', sans-serif;
      font-size:13px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase;
      padding: 10px 20px;
      border: 1px solid ${NAVY};
      background: ${NAVY};
      color: #fff;
      display: inline-flex; align-items: center; gap: 6px;
      overflow: hidden; white-space: nowrap; flex-shrink: 0;
      transition: all 0.35s cubic-bezier(0.22,1,0.36,1);
    }
    .reserve-btn-home::before {
      content: ''; position: absolute; inset: 0;
      background: ${GOLD}; transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0;
    }
    .reserve-btn-home:hover::before { transform: scaleX(1); }
    .reserve-btn-home:hover { border-color: ${GOLD}; color: ${NAVY}; }
    .reserve-btn-home span, .reserve-btn-home svg { position: relative; z-index: 1; }

    .ghost-btn {
      font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase;
      padding: 12px 28px;
      border: 1px solid currentColor;
      display: inline-flex; align-items: center; gap: 8px;
      transition: all 0.35s cubic-bezier(0.22,1,0.36,1);
      position: relative; overflow: hidden;
    }
    .ghost-btn::before {
      content: ''; position: absolute; inset: 0;
      background: currentColor; transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0;
    }
    .ghost-btn:hover::before { transform: scaleX(1); }
    .ghost-btn span, .ghost-btn svg { position: relative; z-index: 1; transition: color 0.35s; }

    .scroll-rail {
      display: flex; gap: 24px;
      overflow-x: auto; scroll-snap-type: x mandatory;
      scroll-behavior: smooth; -webkit-overflow-scrolling: touch;
      padding-bottom: 8px;
    }
    .scroll-rail::-webkit-scrollbar { display: none; }
    .scroll-rail { scrollbar-width: none; }

    .scroll-arrow {
      width: 42px; height: 42px;
      border: 1px solid #e2ddd6;
      display: flex; align-items: center; justify-content: center;
      color:#12100e; background: #fff;
      transition: all 0.3s cubic-bezier(0.22,1,0.36,1);
      cursor: pointer;
    }
    .scroll-arrow:hover { border-color: ${GOLD}; color: ${NAVY}; }
    .scroll-arrow:disabled { opacity: 0.35; cursor: default; }

    .tag-badge-home {
      font-family: 'Jost', sans-serif; font-size:12px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase; padding: 5px 12px;
      background: rgba(8,14,26,0.75); backdrop-filter: blur(8px);
      color: #e2c98a; border: 1px solid rgba(184,151,90,0.35);
    }

    .gold-rule { height: 1px; background: linear-gradient(90deg, ${GOLD} 0%, rgba(184,151,90,0.12) 100%); }

    .card-details-btn {
      font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.14em; text-transform: uppercase;
      padding: 11px 18px; flex-shrink: 0;
      border: 1px solid #ddd9d2; color:#12100e;
      display: inline-flex; align-items: center;
      transition: border-color 0.3s, color 0.3s; white-space: nowrap;
    }
    .card-details-btn:hover { border-color: ${GOLD}; color: ${NAVY}; }

    .dest-card {
      position: relative; overflow: hidden;
      transition: transform 0.4s cubic-bezier(0.22,1,0.36,1);
      cursor: pointer; display: block;
    }
    .dest-card:hover { transform: translateY(-3px); }
    .dest-card:hover .dest-img { transform: scale(1.07); }
    .dest-img {
      position: absolute; inset: 0;
      width: 100%; height: 100%; object-fit: cover;
      transition: transform 0.8s cubic-bezier(0.22,1,0.36,1);
    }

    .dest-no-image {
      position: absolute; inset: 0;
      background: linear-gradient(135deg, #1e2a3a 0%, #0f1827 100%);
    }

    .card-skeleton {
      flex: 0 0 340px; height: 420px;
      background: linear-gradient(90deg, #ede9e3 25%, #e5e1db 50%, #ede9e3 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s infinite;
      scroll-snap-align: start;
    }
    @keyframes shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    .dest-skeleton {
      background: linear-gradient(90deg, #e8e4de 25%, #dedad4 50%, #e8e4de 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s infinite;
    }
  `}</style>
);

/* ─── Helpers ─── */
const RatingStars = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star
        key={i}
        size={9}
        fill={i <= Math.round(rating) ? GOLD : 'none'}
        stroke={i <= Math.round(rating) ? GOLD : '#cbd5e1'}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-3">
    <div className="w-8 h-px" style={{ background: GOLD }} />
    <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
      {text}
    </span>
  </div>
);

/* ─── Tour card ─── */
const FeaturedCard = ({ tour }) => {
  const price    = Math.round(tour.currentPrice || 0);
  const location = (tour.parks || [])
    .map(p => p.country?.name)
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(', ') || tour.category?.name || 'Africa';

  const heroSrc = tour.featuredImage || '';

  return (
    <div className="tour-card-home flex flex-col">
      <div className="tc-img relative">
        {heroSrc
          ? <img src={heroSrc} alt={tour.name} />
          : (
            <div className="tc-img-placeholder">
              <MapPin size={28} strokeWidth={1} style={{ color: 'rgba(184,151,90,0.4)' }} />
            </div>
          )
        }
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        {tour.category?.name && (
          <div className="absolute top-4 left-4">
            <span className="tag-badge-home">{tour.category.name}</span>
          </div>
        )}
        <div className="absolute bottom-4 left-4 flex items-center gap-1.5">
          <MapPin size={10} strokeWidth={1.5} style={{ color: GOLD }} />
          <span className="f-jost text-[13px] text-white/90 font-light">{location}</span>
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1 min-w-0">
        <span className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] block mb-2 truncate" style={{ color: GOLD }}>
          {tour.category?.name || 'Safari'}
        </span>

        <Link to={`/tours/${tour.slug}`}>
          <h3
            className="f-cormorant text-[25px] font-light text-slate-900 leading-snug mb-4 hover:opacity-70 transition-opacity break-words hyphens-auto"
            style={{ lineHeight: '1.2' }}
          >
            {tour.name}
          </h3>
        </Link>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4">
          <div className="flex items-center gap-1">
            <Clock size={10} strokeWidth={1.5} style={{ color: GOLD }} />
            <span className="f-jost text-[13px] text-slate-500 whitespace-nowrap">{tour.durationLabel}</span>
          </div>
          <div className="w-px h-3 bg-slate-200 hidden sm:block" />
          <div className="flex items-center gap-1">
            <Users size={10} strokeWidth={1.5} style={{ color: GOLD }} />
            <span className="f-jost text-[13px] text-slate-500 whitespace-nowrap">Max {tour.maxParticipants}</span>
          </div>
          <div className="flex items-center gap-1 ml-auto flex-shrink-0">
            <RatingStars rating={tour.averageRating} />
            <span className="f-jost text-[13px] text-slate-400 whitespace-nowrap">({tour.totalReviews})</span>
          </div>
        </div>

        <div className="gold-rule mb-4" />

        <div className="flex flex-wrap items-center justify-between gap-3 mt-auto">
          <div className="min-w-0">
            <span className="f-jost text-[12px] uppercase tracking-[0.16em] text-slate-400 block">From</span>
            <div className="flex items-baseline gap-1.5">
              <span className="f-cormorant font-light text-slate-900 leading-none" style={{ fontSize: '25px' }}>
                ${price.toLocaleString()}
              </span>
              <span className="f-jost text-[13px] text-slate-400">/ Person</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to={`/tours/${tour.slug}`} className="card-details-btn">
              Details
            </Link>
            <Link to={`/booking/${tour.slug}`} className="reserve-btn-home" style={{ padding: '12px 22px' }}>
              <span>Reserve</span>
              <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Destination cards ─── */
const DestHero = ({ country }) => {
  const imgSrc = getHeroImage(country);
  return (
    <Link
      to={`/tours?country=${country.slug}`}
      className="dest-card"
      style={{ height: '100%', minHeight: '320px' }}
    >
      {imgSrc
        ? <img src={imgSrc} alt={country.name} className="dest-img" />
        : <div className="dest-no-image" />
      }
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to top, rgba(8,14,26,0.8) 30%, transparent 70%)' }}
      />
      <div className="absolute inset-0 p-8 flex flex-col justify-end">
        <div className="w-6 h-px mb-3" style={{ background: GOLD }} />
        <span className="f-jost text-[13px] uppercase tracking-[0.16em] text-white/60 mb-1">
          {getSubLabel(country)}
        </span>
        <h3 className="f-cormorant text-white font-light" style={{ fontSize: '38px', lineHeight: 1 }}>
          {country.name}
        </h3>
        <span className="f-jost text-[13px] text-white/50 mt-2">
          {country.tourCount > 0
            ? `${country.tourCount} ${country.tourCount === 1 ? 'safari' : 'safaris'}`
            : 'Tailor-made journeys'}
        </span>
      </div>
    </Link>
  );
};

const DestSmall = ({ country }) => {
  const imgSrc = getHeroImage(country);
  return (
    <Link
      to={`/tours?country=${country.slug}`}
      className="dest-card relative overflow-hidden"
      style={{ minHeight: '100px' }}
    >
      {imgSrc
        ? <img src={imgSrc} alt={country.name} className="dest-img" />
        : <div className="dest-no-image" />
      }
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to right, rgba(8,14,26,0.75) 40%, rgba(8,14,26,0.2))' }}
      />
      <div className="absolute inset-0 p-6 flex flex-col justify-center">
        <span className="f-jost text-[12px] uppercase tracking-[0.16em] text-white/50 mb-1">
          {getSubLabel(country)}
        </span>
        <h3 className="f-cormorant text-white font-light text-3xl leading-none">
          {country.name}
        </h3>
        <span className="f-jost text-[13px] text-white/40 mt-1">
          {country.tourCount > 0
            ? `${country.tourCount} ${country.tourCount === 1 ? 'safari' : 'safaris'}`
            : 'Tailor-made journeys'}
        </span>
      </div>
    </Link>
  );
};

/* ─── Stat pill — used in both the hero strip and philosophy grid ─── */
const StatPill = ({ num, label }) => (
  <div>
    <p className="f-cormorant text-white text-[36px] font-light leading-none">{num}</p>
    <p className="f-jost text-[13px] uppercase tracking-[0.16em] mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
      {label}
    </p>
  </div>
);

/* ─── Philosophy grid cell ─── */
const PhilosophyCell = ({ num, label }) => (
  <div
    className="p-8 flex flex-col gap-2"
    style={{
      background:   'rgba(255,255,255,0.02)',
      borderRight:  '1px solid rgba(255,255,255,0.05)',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}
  >
    <p className="f-cormorant text-white font-light" style={{ fontSize: '44px', lineHeight: 1, color: GOLD }}>
      {num}
    </p>
    <p className="f-jost text-[13px] uppercase tracking-[0.14em] text-slate-500">{label}</p>
  </div>
);

/* ─── Film lightbox — opened from the hero "Watch Film" button ─── */
const FilmModal = ({ isOpen, onClose, videoUrl, poster }) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[10050] flex items-center justify-center px-5"
      style={{ background: 'rgba(5,8,15,0.94)', backdropFilter: 'blur(8px)', animation: 'filmFadeIn 0.3s cubic-bezier(0.22,1,0.36,1) both' }}
    >
      <style>{`
        @keyframes filmFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes filmScaleIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      <button
        onClick={onClose}
        aria-label="Close film"
        className="absolute flex items-center justify-center"
        style={{ top: '24px', right: '24px', width: '44px', height: '44px', border: '1px solid rgba(255,255,255,0.25)', background: 'transparent', color: '#fff', cursor: 'pointer', transition: 'all 0.25s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#fff'; }}
      >
        <X size={18} strokeWidth={1.5} />
      </button>

      <div
        onClick={e => e.stopPropagation()}
        className="w-full"
        style={{ maxWidth: '1120px', animation: 'filmScaleIn 0.4s cubic-bezier(0.22,1,0.36,1) both' }}
      >
        <video
          key={videoUrl}
          src={videoUrl}
          poster={poster}
          controls
          autoPlay
          playsInline
          className="w-full h-auto"
          style={{ display: 'block', background: '#000', boxShadow: '0 40px 120px -20px rgba(0,0,0,0.6)' }}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   HOME PAGE
═══════════════════════════════════════════ */
const HomePage = () => {
  const searchBarRef = useRef(null);
  const railRef      = useRef(null);
  const [filmOpen, setFilmOpen] = useState(false);

  const { registerTrigger, clearTrigger } = useTourBuilder();

  const { data: toursData,     loading: toursLoading     } = useQuery(GET_HOME_FEATURED_TOURS, { fetchPolicy: 'cache-first' });
  const { data: countriesData, loading: countriesLoading } = useQuery(GET_TOUR_COUNTRIES,      { fetchPolicy: 'cache-first' });
  const { data: settingsData                             } = useQuery(GET_SITE_SETTINGS,        { fetchPolicy: 'cache-first' });
  const { data: statsData                                } = useQuery(GET_ABOUT_STATS,          { fetchPolicy: 'cache-first' });

  const featuredTours  = toursData?.homeFeaturedTours  || [];
  const tourCountries  = countriesData?.tourCountries  || [];
  const settings       = settingsData?.siteSettings    || {};
  const stats          = statsData?.aboutStats         || {};

  const heroCountry    = tourCountries[0]        || null;
  const smallCountries = tourCountries.slice(1, 4);
  const { data: aboutData } = useQuery(GET_ABOUT_PAGE_SETTINGS, { fetchPolicy: 'cache-first' });
  const about = aboutData?.aboutPageSettings || {};
  const { data: partnersData } = useQuery(GET_PRESS_PARTNERS, { fetchPolicy: 'cache-first' });
  const trustedPartners = partnersData?.pressPartners || [];

  // ── Hero stats strip — derived from live data where possible ──────────────
  //   totalTours  → "120+" bucket built from stats.totalTours
  //   totalCountries → stats.totalCountries (exact count)
  //   Founded year is static product identity, but pulled from establishedLabel
  //   as a fallback so the DB can override it.
  const heroStats = [
    {
      num:   stats.totalTours   ? `${stats.totalTours}+` : '—',
      label: 'Safaris',
    },
    {
      num:   stats.totalCountries ? String(stats.totalCountries) : '—',
      label: 'Countries',
    },
    {
      // Derive the founding year from yearsOperating so marketing can update
      // just the FOUNDING_YEAR constant in the backend without touching the UI.
      num:   stats.yearsOperating
               ? String(new Date().getFullYear() - stats.yearsOperating)
               : '—',
      label: 'Est.',
    },
  ];

  // ── Philosophy grid — driven by live stats ────────────────────────────────
  const philosophyStats = [
    {
      num:   stats.yearsOperating ? `${stats.yearsOperating}+` : '—',
      label: 'Years planning safaris',
    },
    {
      num:   stats.totalCountries ? String(stats.totalCountries) : '—',
      label: 'Countries in the collection',
    },
    {
      num:   stats.travellers || '—',
      label: 'Travellers served',
    },
    {
      num:   stats.averageRating ? stats.averageRating.toFixed(1) : '—',
      label: 'Average guest rating',
    },
  ];

  const handleTriggerBuilder = () => {
    if (searchBarRef.current) searchBarRef.current.handleExternalTrigger();
  };

  useEffect(() => {
    registerTrigger(handleTriggerBuilder);
    return () => clearTrigger();
  }, [registerTrigger, clearTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollRail = (dir) => {
    if (railRef.current) {
      railRef.current.scrollBy({ left: dir === 'left' ? -380 : 380, behavior: 'smooth' });
    }
  };

  return (
    <div className="f-jost" style={{ background: '#F5F3EF' }}>
      <HomeStyles />

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section className="relative flex items-start sm:items-center overflow-hidden dark-sec" style={{ background: NAVY, minHeight: '620px', height: '88vh', maxHeight: '880px' }}>
        <div className="absolute inset-0">
          <div
            className="w-full h-full"
            style={{ background: 'linear-gradient(135deg, #0d1628 0%, #080e1a 60%, #0a1220 100%)' }}
          />
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={HERO_BG_VIDEO}
            poster={HERO_POSTER}
            autoPlay
            loop
            muted
            playsInline
            style={{ opacity: 0.85 }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(135deg, rgba(13,22,40,0.28) 0%, rgba(8,14,26,0.62) 60%, rgba(10,18,32,0.45) 100%)' }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(105deg, rgba(8,14,26,0.55) 35%, rgba(8,14,26,0.15) 100%)' }}
          />
        </div>

        <div className="absolute inset-0 grain-overlay pointer-events-none" />

        <div
          className="absolute left-0 top-0 bottom-0 w-px hidden lg:block"
          style={{
            background: `linear-gradient(to bottom, transparent, ${GOLD} 30%, ${GOLD} 70%, transparent)`,
            marginLeft: '10%',
            opacity: 0.25,
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-32 sm:pt-0 w-full">
          <div className="max-w-4xl">
            {/* Eyebrow — "Premium Expeditions" is product positioning copy, fine to keep static
                but could be stored in SiteSettings.brand_statement if needed.
                Section is items-start + pt-32 on mobile (~98px navbar + gap) instead of
                items-center: centering a padded item only pushes it down by half the
                padding, which wasn't enough to clear the fixed transparent navbar. */}
            <div className="hero-line-1 flex items-center gap-3 mb-7">
              <div className="w-8 h-px" style={{ background: GOLD }} />
              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                Private Safaris · East Africa
              </span>
            </div>

            <h1
              className="hero-line-2 f-cormorant text-white font-light leading-[0.9] mb-3"
              style={{ fontSize: 'clamp(56px,7.5vw,96px)', textShadow: '0 2px 20px rgba(0,0,0,0.55), 0 0 60px rgba(0,0,0,0.3)' }}
            >
              Your safari,
            </h1>
            <h1
              className="hero-line-3 f-cormorant font-light leading-[0.9] mb-8"
              style={{ fontSize: 'clamp(56px,7.5vw,96px)', color: GOLD, textShadow: '0 2px 20px rgba(0,0,0,0.55), 0 0 60px rgba(0,0,0,0.3)' }}
            >
              your way.
            </h1>

            {/* Brand statement pulled from SiteSettings */}
            <p className="hero-line-3 f-jost text-slate-400 text-[16px] font-light leading-relaxed mb-10 max-w-md" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}>
              {settings.brandStatement ||
                'Private, guided safaris across Tanzania and East Africa — planned around the season, the wildlife and the way you like to travel.'}
            </p>

            <div className="hero-line-4 flex flex-wrap items-center gap-5">
              <button
                onClick={handleTriggerBuilder}
                className="ghost-btn"
                style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
                onMouseEnter={e => { e.currentTarget.querySelector('span').style.color = NAVY; }}
                onMouseLeave={e => { e.currentTarget.querySelector('span').style.color = '#fff'; }}
              >
                <span>Plan Your Own</span>
                <ArrowRight size={12} />
              </button>

              <button className="flex items-center gap-3 group" onClick={() => setFilmOpen(true)}>
                <div
                  className="w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-300 group-hover:border-[#B8975A]"
                  style={{ borderColor: 'rgba(255,255,255,0.2)' }}
                >
                  <PlayCircle size={20} style={{ color: GOLD }} />
                </div>
                <span className="f-jost text-[13px] font-medium uppercase tracking-[0.14em] text-white/70 group-hover:text-white transition-colors">
                  Watch Film
                </span>
              </button>
            </div>

            {/* ── Hero stats strip — live data ── */}
            <div
              className="hero-line-4 flex items-center gap-8 mt-14 pt-8 border-t"
              style={{ borderColor: 'rgba(255,255,255,0.08)' }}
            >
              {heroStats.filter(({ num }) => num && num !== '—' && num !== '0').map(({ num, label }) => (
                <StatPill key={label} num={num} label={label} />
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
          <div className="w-px h-10 animate-pulse" style={{ background: GOLD }} />
          <span className="f-jost text-[12px] uppercase tracking-[0.2em] text-white">Scroll</span>
        </div>
      </section>

      {/* ── Search bar overlap ── */}
      <div className="relative -mt-10 max-w-5xl mx-auto px-5 sm:px-8" style={{ zIndex: 100 }}>
        <ProfessionalSearchBar ref={searchBarRef} />
      </div>

      {/* ══════════════════════════════════════
          FEATURED TOURS
      ══════════════════════════════════════ */}
      <section className="py-20 lg:py-28 overflow-hidden" style={{ background: '#F5F3EF' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
            <div>
              <SectionLabel text="Featured Safaris" />
              <h2
                className="f-cormorant font-light leading-none"
                style={{ fontSize: 'clamp(40px,4vw,54px)', color: NAVY }}
              >
                Our Most-Loved Safaris
              </h2>
            </div>
            <div className="flex items-center gap-3 sm:mb-1">
              <button onClick={() => scrollRail('left')} className="scroll-arrow" aria-label="Scroll left">
                <ChevronLeft size={16} strokeWidth={1.5} />
              </button>
              <button onClick={() => scrollRail('right')} className="scroll-arrow" aria-label="Scroll right">
                <ChevronRight size={16} strokeWidth={1.5} />
              </button>
              <Link
                to="/tours"
                className="ghost-btn ml-2"
                style={{ color: NAVY, borderColor: '#ccc' }}
                onMouseEnter={e => { e.currentTarget.querySelectorAll('span, svg').forEach(el => el.style.color = '#fff'); }}
                onMouseLeave={e => { e.currentTarget.querySelectorAll('span, svg').forEach(el => el.style.color = NAVY); }}
              >
                <span style={{ color: NAVY }}>View All</span>
                <ArrowRight size={11} style={{ color: NAVY }} />
              </Link>
            </div>
          </div>

          <div ref={railRef} className="scroll-rail pb-4">
            {toursLoading && [1, 2, 3].map(i => (
              <div key={i} className="card-skeleton" />
            ))}

            {!toursLoading && featuredTours.map(tour => (
              <FeaturedCard key={tour.id} tour={tour} />
            ))}

            {!toursLoading && (
              <div
                className="flex-shrink-0 flex flex-col items-center justify-center gap-4 border"
                style={{ flex: '0 0 240px', background: NAVY, borderColor: 'rgba(184,151,90,0.2)' }}
              >
                <div className="w-8 h-px" style={{ background: GOLD }} />
                <p className="f-cormorant text-white text-2xl text-center leading-snug px-6">
                  {featuredTours.length > 0
                    ? `${featuredTours.length}+ more safaris`
                    : 'Browse all safaris'}
                </p>
                <Link to="/tours" className="reserve-btn-home mt-2">
                  <span>Explore All</span>
                  <ArrowRight size={10} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          DESTINATIONS
      ══════════════════════════════════════ */}
      <section className="py-20 lg:py-28" style={{ background: '#fff' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="mb-12">
            <SectionLabel text="Destinations" />
            <h2
              className="f-cormorant font-light leading-none"
              style={{ fontSize: 'clamp(40px,4vw,54px)', color: NAVY }}
            >
              Where Will You Go?
            </h2>
          </div>

          {countriesLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 h-auto md:h-[580px]">
              <div className="dest-skeleton" style={{ minHeight: '320px' }} />
              <div className="grid grid-rows-3 gap-3 h-full">
                {[1,2,3].map(i => <div key={i} className="dest-skeleton" style={{ minHeight: '100px' }} />)}
              </div>
            </div>
          )}

          {!countriesLoading && heroCountry && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 h-auto md:h-[580px]">
              <DestHero country={heroCountry} />
              <div
                className="grid gap-3 h-full"
                style={{ gridTemplateRows: `repeat(${Math.max(smallCountries.length, 1)}, 1fr)` }}
              >
                {smallCountries.length > 0
                  ? smallCountries.map(c => <DestSmall key={c.id} country={c} />)
                  : (
                    <div
                      className="relative overflow-hidden flex flex-col items-start justify-end p-8"
                      style={{ background: NAVY, border: '1px solid rgba(184,151,90,0.2)' }}
                    >
                      <div className="w-6 h-px mb-3" style={{ background: GOLD }} />
                      <p
                        className="f-cormorant text-white font-light mb-4"
                        style={{ fontSize: '32px', lineHeight: 1.1 }}
                      >
                        More destinations<br />coming soon
                      </p>
                      <Link to="/tours" className="reserve-btn-home">
                        <span>Browse All</span>
                        <ArrowRight size={10} />
                      </Link>
                    </div>
                  )
                }
              </div>
            </div>
          )}

          {!countriesLoading && !heroCountry && (
            <div className="flex items-center justify-center py-20">
              <p className="f-jost text-[15px] text-slate-400 uppercase tracking-[0.16em]">
                Destinations coming soon
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════
          TRUSTED BY
      ══════════════════════════════════════ */}
      {trustedPartners.length > 0 && (
        <section className="border-t border-b py-10" style={{ borderColor: '#e2ddd6', background: '#F5F3EF' }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <p className="f-jost text-[13px] uppercase tracking-[0.2em] text-slate-400 flex-shrink-0">Trusted by</p>
              <div className="w-px h-5 bg-slate-300 hidden sm:block" />
              {trustedPartners.map((p) => (
                p.logoUrl ? (
                  <img key={p.id} src={p.logoUrl} alt={p.name} style={{ height: '24px', opacity: 0.5, filter: 'grayscale(100%)' }} />
                ) : (
                  <span key={p.id} className="f-cormorant text-slate-400 font-light" style={{ fontSize: '22px' }}>{p.name}</span>
                )
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          PHILOSOPHY STRIP
      ══════════════════════════════════════ */}
      <section className="py-20 lg:py-28 overflow-hidden dark-sec" style={{ background: NAVY }}>
        <div className="grain-overlay absolute inset-0 pointer-events-none opacity-50" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <SectionLabel text={about.philosophyEyebrow || 'Our Philosophy'} />
              {/* Pull quote from AboutPageSettings if available, otherwise static fallback */}
              <h2
                className="f-cormorant text-white font-light leading-[0.95] mb-6"
                style={{ fontSize: 'clamp(40px,4vw,56px)' }}
              >
                {about.philosophyHeading || 'Travel is not a destination. It is a way of seeing.'}
              </h2>
              {/* Brand statement repurposed as philosophy body — replace with a dedicated
                  philosophySub field from AboutPageSettings once the About page is wired up */}
                  <p className="f-jost text-slate-400 text-[15px] font-light leading-relaxed max-w-sm mb-8">
                  {about.philosophySub ||
                    `Every safari we plan starts with the same questions: when are you going, what do you
                    most want to see, and how do you like to travel? Then we match the parks, the camps
                    and the route to the season — so you're in the right place at the right time.`}
                </p>
              <Link
                to="/about"
                className="ghost-btn"
                style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.25)' }}
                onMouseEnter={e => e.currentTarget.querySelector('span').style.color = NAVY}
                onMouseLeave={e => e.currentTarget.querySelector('span').style.color = '#fff'}
              >
                <span>Our Story</span>
                <ArrowRight size={11} />
              </Link>
            </div>

            {/* ── Philosophy stats grid — live data ── */}
            <div className="grid grid-cols-2 gap-px" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
              {/* An odd count would leave a hole in the 2-up grid — the last
                  cell spans both columns instead. */}
              {philosophyStats.filter(({ num }) => num && num !== '—' && num !== '0').map(({ num, label }, i, all) => (
                <div key={label} className={all.length % 2 && i === all.length - 1 ? 'col-span-2' : ''}>
                  <PhilosophyCell num={num} label={label} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FINAL CTA STRIP
      ══════════════════════════════════════ */}
      <section className="py-20" style={{ background: '#F5F3EF', borderTop: '1px solid #e2ddd6' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <div>
            {/* ctaEyebrow from AboutPageSettings — "Begin" is the default on the model */}
            <SectionLabel text="Begin" />
            {/* ctaHeading from AboutPageSettings — default "Ready for your next chapter?" */}
            <h2
              className="f-cormorant font-light"
              style={{ fontSize: 'clamp(32px,3vw,42px)', color: NAVY }}
            >
              Ready to plan your safari?
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-4 flex-shrink-0 w-full sm:w-auto">
            <Link
              to="/contact"
              className="ghost-btn"
              style={{ color: NAVY, borderColor: '#ccc' }}
              onMouseEnter={e => e.currentTarget.querySelectorAll('span').forEach(el => el.style.color = '#fff')}
              onMouseLeave={e => e.currentTarget.querySelectorAll('span').forEach(el => el.style.color = NAVY)}
            >
              <span>Speak to a Specialist</span>
            </Link>
            <Link to="/tours" className="reserve-btn-home" style={{ padding: '14px 28px' }}>
              <span>Browse Safaris</span>
              <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </section>

      <FilmModal
        isOpen={filmOpen}
        onClose={() => setFilmOpen(false)}
        videoUrl={HERO_FILM_VIDEO}
        poster={HERO_POSTER}
      />
    </div>
  );
};

export default HomePage;